import React, { useState } from 'react';
import { Formik } from 'formik';
import * as Yup from 'yup';
import Input from '../../../../../components/form-control/Input';
import Button from '../../../../../components/form-control/Button';
import SelectDropdown from '../../../../../components/form-control/SelectDropdown';
import DatePickerInput from '../../../../../components/form-control/DatePickerInput';
import { PAYMENT_CHANNELS, PAYMENT_PROCESSORS } from '../../../../../resources/enums';
import { getFormattedPaymentChannel, showToast } from '../../../../../utils/miscFunctions';
import { completeNonMotorPolicyPayment, getNonMotorPolicyByPolicyNumber, submitPaymentReceipt } from '../../../../../api/requests';
import moment from 'moment';
import SelectMenu from '../../../../../components/form-control/SelectMenu';
import { BANKS_OF_GHANA, INPUT_DATE_FORMAT } from '../../../../../resources/constants';
import FileUpload from '../../../../../components/elements/FileUpload/FileUpload';
import { IEditPaymentDTO } from '../../../payments/PaymentEditModal';
import useImage from '../../../../../hooks/imageHook';
import { FileTextIcon, Receipt } from 'lucide-react';

interface ICompleteNonAutoPolicyPaymentProps {
  policy: any;
  onProceed: () => void;
  onCancel: () => void;
  isUpdateModal?: boolean;
}

export interface ICompleteNonAutoPolicyPaymentDTO {
  paymentAmount: number;
  paymentChannel: string;
  paymentDate: string;
  paymentReference: string;
  trackCommission?: string;
}

interface IPaymentReceipt{
  receipt: File | null;
}


const CompleteNonAutoPolicyPayment = ({ policy, onProceed, onCancel, isUpdateModal=false }: ICompleteNonAutoPolicyPaymentProps) => {
  const [submitting, setSubmitting] = React.useState<boolean>(false);
  const isPayment = isUpdateModal ? policy?.payments[0] && !policy?.payments[0].length : policy?.payments && policy?.payments !== null
  const BankList: Array<string> = Object.values(BANKS_OF_GHANA);
  const [isBankOrBankTranfertSelected, setIsBankOrBankTranfertSelected] = useState<boolean>(false);
  const ProcessorList: Array<string> = Object.values(PAYMENT_PROCESSORS);
  const paymentChannels = Object.keys(PAYMENT_CHANNELS).map((channel) => ({
    name: getFormattedPaymentChannel(channel),
    value: channel
  }));
  const [applicationDocumentFiles, setApplicationDocumentFiles] = useState<File | null>();
  const { getImageFormData } = useImage();
  const handleSubmit = async (values: any) => {
    const currentTime = moment().format('HH:mm');
    const payload: ICompleteNonAutoPolicyPaymentDTO = {
      ...values,
      paymentDate: moment(values.paymentDate).format('YYYY-MM-DD'),
      trackCommission: values.trackCommission === 'Yes' ? true : false
    };

    const paymentReceiptPayload:IEditPaymentDTO = {
      bank: values.bank || '',
      reference: values.paymentReference || '',
      channel:  values.paymentChannel,
      processor: values.processor,
      narration: 'Paid',
      dateOfPayment: moment(values.paymentDate).format('YYYY-MM-DD'),
      timeOfPayment: currentTime,
      dueDate: moment(values.paymentDate).format(INPUT_DATE_FORMAT)
    }
    setSubmitting(true);

    try {
      if (!policy?.policyNumber) return;
      const response: any = await completeNonMotorPolicyPayment(policy.policyNumber, payload);
      showToast.success(response?.message || 'Policy marked as paid successfully');
      
      if(response && applicationDocumentFiles){

       const policyResponse = response && await getNonMotorPolicyByPolicyNumber(response?.policy?.policyNumber);
       if(policyResponse && policyResponse?.payments && policyResponse?.payments.length > 0){
        const file = getImageFormData(applicationDocumentFiles)
        const paymentId = policyResponse?.payments[0]?.id
          await submitPaymentReceipt(paymentId, paymentReceiptPayload, file)
          .then((response:any) =>{})
          .catch((error:Error) => {console.log("err",error)});
       }
      
      }
      
      onProceed();

    } catch (error: any) {
      showToast.error(error?.description);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl dark:bg-darkBg">
      <Formik
        initialValues={{
          paymentAmount: policy?.grossPremium || '',
          paymentChannel: isPayment ? policy?.payments[0]?.channel : '',
          paymentDate: isPayment ? String(policy?.payments[0]?.dateOfPayment) : undefined,
          paymentReference:  isPayment ? policy?.payments[0]?.reference : '',
          trackCommission: '',
          bank:  isPayment? policy?.payments[0]?.bankName : '',
          processor:  isPayment ? policy?.payments[0]?.processor : ''
        }}
        validateOnBlur
        validationSchema={Yup.object({
          paymentChannel: Yup.string().required('Payment channel is required.'),
          paymentDate: Yup.string().nullable().required('Payment date is required.'),
          paymentReference: Yup.string().required('Payment reference is required.'),
          trackCommission: Yup.string().required("Selecting track commission is required"),
          processor:  Yup.string().required("Processor is required"),
          bank: isBankOrBankTranfertSelected ? Yup.string().required("Selecting a bank is required") : Yup.string().notRequired(),
        })}
        onSubmit={(values) => {
          handleSubmit(values);
        }}>
        {({ values, errors, handleSubmit, handleChange, handleBlur, touched, setFieldValue }) => (
          <form className="space-y-6 p-6" onSubmit={handleSubmit}>

            <div className="space-y-4">
              <h3 className='flex gap-2 items-center text-lg text-gray-600 dark:text-darkBg-mainText'><FileTextIcon className='h-8 w-8 text-green-400' />Payment Details</h3>
              <Input
                type="number"
                name="paymentAmount"
                label="Payment amount"
                value={values.paymentAmount}
                placeholder="Eg: 1000"
                wrapperClasses=""
                disabled
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.paymentAmount ? errors.paymentAmount : undefined}
                helperText={touched?.paymentAmount ? errors.paymentAmount : undefined}
              />

              <SelectDropdown
                name="paymentChannel"
                label="Payment channel"
                listSelectedValue={values.paymentChannel}
                list={paymentChannels}
                onChange={(value: string) => {
                  
                  setFieldValue('paymentChannel', value)
                  if(value === PAYMENT_CHANNELS.BANK || value === PAYMENT_CHANNELS.BANK_TRANSFER){
                    setIsBankOrBankTranfertSelected(true)
                  }else{
                    setIsBankOrBankTranfertSelected(false)
                  }
                  
                }}
                required
                onBlur={handleBlur}
                error={touched?.paymentChannel ? errors.paymentChannel : undefined}
                helperText={touched?.paymentChannel ? errors.paymentChannel : undefined}
              />

              <SelectMenu
                  name="processor"
                  list={ProcessorList}
                  listSelectedValue={values.processor}
                  onBlur={handleBlur}
                  onChange={(value: string) => {
                    setFieldValue('processor', value);
                  }}
                  label="Processor"
                  placeholder="Processor"
                  required
                  error={touched?.processor ? errors.processor : undefined}
                 helperText={touched?.processor ? errors.processor : undefined}
                />

               {(values.paymentChannel === PAYMENT_CHANNELS.BANK || values.paymentChannel === PAYMENT_CHANNELS.BANK_TRANSFER)  && (
                  <SelectMenu
                    name="bank"
                    list={BankList}
                    listSelectedValue={values.bank}
                    onBlur={handleBlur}
                    onChange={(value: string) => {
                      setFieldValue('bank', value);
                      
                    }}
                    label="Bank Name"
                    placeholder="Bank Name"
                    error={touched?.bank ? errors.bank : undefined}
                    helperText={touched?.bank ? errors.bank : undefined}
                  />
                )}

              <DatePickerInput
                id={'paymentDate'}
                selected={values.paymentDate ? new Date(values.paymentDate) : null}
                label="Payment date"
                onChange={(date: Date | null) => {
                 setFieldValue('paymentDate', date);
                }}
                dateFormat = "dd/MM/yyyy"
                allowFutureSelections= {false}
                required
                error={touched?.paymentDate ? errors.paymentDate : undefined}
                helperText={touched?.paymentDate ? errors.paymentDate : undefined}
              />

              <Input
                type="text"
                name="paymentReference"
                label="Payment reference"
                value={values.paymentReference}
                placeholder="Eg: 1000"
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched?.paymentReference ? errors.paymentReference : undefined}
                helperText={touched?.paymentReference ? errors.paymentReference : undefined}
                required
              />

              <SelectMenu
                name="trackCommission"
                label="Track Commission"
                listSelectedValue={values.trackCommission}
                onChange={(value: string) => { setFieldValue('trackCommission', value) }}
                list={["Yes", "No"]}
                required
                error={touched.trackCommission && errors.trackCommission !== undefined ? String(errors.trackCommission) : undefined}
                helperText={touched.trackCommission && errors.trackCommission !== undefined ? String(errors.trackCommission) : undefined}
              />
            </div>
            <hr/>
            <h3 className='flex gap-2 items-center text-lg text-gray-600 dark:text-darkBg-mainText'><Receipt className='h-8 w-8 text-green-400' />Payment Receipt</h3>
              <div className="flex gap-2 w-4/5 mx-auto border border-dashed border-green-400 p-6 rounded-lg">
                 <FileUpload
                    onFileLoad={(doc: any) => {
                        if (doc && doc[0]) {
                          setApplicationDocumentFiles(doc[0])
                        }else{
                         // setApplicationDocumentFiles(policy?.payments[0]?.evidenceUrl)
                        }
                    }}
                    label=""
                    maxHeight={350}
                    maxWidth={300}
                    imageContainerWidth="100%"
                    cropImage={true}
                    onFileDeleted={() => {
                       
                    }}
                    showFileMax={false}
                    savedImage={isPayment ? policy?.payments[0]?.evidenceUrl : null}
                />
              </div>

            <div className="w-full flex gap-4">
              <Button
                label= {isUpdateModal ? "Cancel" : "Do it later"}
                bgColor='secondary'
                  fullWidth
                onClick={onCancel}
              />
              <Button
                label="Confirm"
                onClick={handleSubmit}
                bgColor='primary'
              fullWidth
                loading={submitting}
                disabled={isUpdateModal ? false : applicationDocumentFiles ? false : true }
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
};

export default CompleteNonAutoPolicyPayment;
